import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, FolderOpen, Trash2, ChevronDown } from 'lucide-react';
import { useProjects, useDeleteProject, type ProjectListDTO } from '../../hooks/useProjects';
import { useAuthStore } from '../../stores/auth-store';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge, StatusBadge } from '../../components/ui/Badge';
import { SkeletonTable } from '../../components/ui/Skeleton';

// States that count as "En Ejecución" in the grouped view (proforma_aprobada has its own separate section)
const EJECUCION_STATES = ['en_ejecucion'];
// States that count as early stage in the grouped view
const INICIO_STATES = ['contacto_inicial', 'datos_listos', 'proforma_generada', 'proforma_en_revision', 'cancelado'];

type GroupFilter = '' | 'ejecucion' | 'inicio' | 'finalizado' | 'proforma_aprobada' | 'pre_liquidacion';

const GROUP_FILTER_OPTIONS: { key: GroupFilter; label: string }[] = [
  { key: 'ejecucion',         label: 'En Ejecución' },
  { key: 'proforma_aprobada', label: 'Aprobado' },
  { key: 'pre_liquidacion',   label: 'Pre Liquidación' },
  { key: 'inicio',            label: 'Contacto Inicial' },
  { key: 'finalizado',        label: 'Finalizado' },
];

export function ProjectListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isResidente = user?.role === 'residente';
  const canCreate = ['administrativo', 'gerente', 'operaciones', 'residente'].includes(user?.role ?? '');

  const [groupFilter, setGroupFilter] = useState<GroupFilter>('');
  const [showEstadoDropdown, setShowEstadoDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout>>();
  const [page, setPage] = useState(1);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
      setDebounceTimer(timer);
    },
    [debounceTimer],
  );

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      // ejecucion maps to en_ejecucion; other single-state groups filter directly; inicio/all use no filter
      estado: groupFilter === 'ejecucion'
        ? 'en_ejecucion'
        : ['finalizado', 'proforma_aprobada', 'pre_liquidacion'].includes(groupFilter)
          ? groupFilter
          : undefined,
      page,
      limit: 500,
    }),
    [debouncedSearch, groupFilter, page],
  );

  const { data, isLoading, isError, refetch } = useProjects(filters);

  // Client-side filtering only needed for 'inicio' (multiple states); others are server-side
  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (groupFilter === 'inicio') return data.data.filter((p) => INICIO_STATES.includes(p.estado));
    // all other filters (ejecucion, finalizado, etc.) are already filtered server-side
    return data.data;
  }, [data?.data, groupFilter]);

  // Grouped sub-lists used when no filter is active
  const ejecucionProjects = useMemo(
    () => filteredData.filter((p) => EJECUCION_STATES.includes(p.estado)),
    [filteredData],
  );
  const inicioProjects = useMemo(
    () => filteredData.filter((p) => INICIO_STATES.includes(p.estado)),
    [filteredData],
  );
  const aprobadoProjects = useMemo(
    () => filteredData.filter((p) => p.estado === 'proforma_aprobada'),
    [filteredData],
  );
  const preliquidacionProjects = useMemo(
    () => filteredData.filter((p) => p.estado === 'pre_liquidacion'),
    [filteredData],
  );
  const finalizadoProjects = useMemo(
    () => filteredData.filter((p) => p.estado === 'finalizado'),
    [filteredData],
  );

  const title = isResidente ? 'Mis Proyectos' : 'Proyectos';

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="space-y-6 fade-up-1">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-brand-dark">{title}</h1>
        </div>
        <Card><div className="p-6"><SkeletonTable rows={5} cols={5} /></div></Card>
      </div>
    );
  }

  // ── Error ──
  if (isError) {
    return (
      <div className="space-y-6 fade-up-1">
        <h1 className="text-2xl font-bold text-brand-dark">{title}</h1>
        <Card>
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">Error al cargar proyectos</p>
            <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
              Reintentar
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Unified view (all roles) ──
  return (
    <div className="space-y-6 fade-up-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-dark">{title}</h1>
        {canCreate && (
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => navigate('/proyectos/nuevo')}
            className="hidden sm:flex"
          >
            Nuevo Proyecto
          </Button>
        )}
      </div>

      {/* Search + Estado filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            placeholder="Buscar por nombre o código..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {/* Estado filter dropdown */}
        <div className="relative">
          <button
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors ${
              groupFilter
                ? 'border-brand-blue bg-brand-blue/5 text-brand-blue'
                : 'border-gray-200 bg-white/80 text-gray-600 hover:border-gray-300'
            }`}
            onClick={() => setShowEstadoDropdown((v) => !v)}
          >
            {groupFilter ? GROUP_FILTER_OPTIONS.find((o) => o.key === groupFilter)?.label : 'Estado'}
            <ChevronDown size={14} />
          </button>
          {showEstadoDropdown && (
            <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
              <button
                className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
                onClick={() => { setGroupFilter(''); setShowEstadoDropdown(false); }}
              >
                Todos
              </button>
              <div className="border-t border-gray-100" />
              {GROUP_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    groupFilter === opt.key ? 'font-medium text-brand-blue' : 'text-gray-700'
                  }`}
                  onClick={() => { setGroupFilter(opt.key); setShowEstadoDropdown(false); setPage(1); }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Active filter chip */}
      {groupFilter && (
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-1 rounded-full bg-brand-blue/10 px-2.5 py-1 text-xs font-medium text-brand-blue hover:bg-brand-blue/20"
            onClick={() => setGroupFilter('')}
          >
            {GROUP_FILTER_OPTIONS.find((o) => o.key === groupFilter)?.label} ✕
          </button>
        </div>
      )}

      {/* Content */}
      {filteredData.length === 0 ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <FolderOpen size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">
              {debouncedSearch || groupFilter
                ? 'Sin resultados'
                : isResidente
                  ? 'No tienes proyectos asignados'
                  : 'No hay proyectos aún'}
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              {debouncedSearch || groupFilter
                ? 'Prueba con otro término o limpia los filtros.'
                : isResidente
                  ? 'Cuando te asignen un proyecto aparecerá aquí.'
                  : 'Crea el primer proyecto para comenzar a gestionar obras.'}
            </p>
            {!debouncedSearch && !groupFilter && canCreate && !isResidente && (
              <Button
                variant="primary"
                className="mt-4"
                leftIcon={<Plus size={16} />}
                onClick={() => navigate('/proyectos/nuevo')}
              >
                Crear Proyecto
              </Button>
            )}
          </div>
        </Card>
      ) : groupFilter || debouncedSearch ? (
        /* Filtered: flat list */
        <>
          {!isResidente && (
            <Card className="hidden sm:block">
              <div className="max-h-96 overflow-x-auto overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-3">Proyecto</th>
                      <th className="px-4 py-3">Rubros</th>
                      <th className="px-4 py-3">Residente</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="w-10 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((project) => (
                      <ProjectRow key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          <div className={`${isResidente ? 'space-y-3' : 'space-y-3 sm:hidden'} max-h-96 overflow-y-auto pr-1`}>
            {filteredData.map((project) => (
              <ProjectCard key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
            ))}
          </div>
        </>
      ) : (
        /* Default: two grouped sections */
        <div className="space-y-6">
          {/* En Ejecución */}
          {ejecucionProjects.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                  En Ejecución
                  <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    {ejecucionProjects.length}
                  </span>
                </h2>
              </div>
              {!isResidente && (
                <Card className="hidden sm:block">
                  <div className="max-h-96 overflow-x-auto overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          <th className="px-4 py-3">Proyecto</th>
                          <th className="px-4 py-3">Rubros</th>
                          <th className="px-4 py-3">Residente</th>
                          <th className="px-4 py-3">Estado</th>
                          <th className="w-10 px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {ejecucionProjects.map((project) => (
                          <ProjectRow key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
              <div className={`${isResidente ? 'space-y-3' : 'space-y-3 sm:hidden'} max-h-96 overflow-y-auto pr-1`}>
                {ejecucionProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                ))}
              </div>
            </div>
          )}

          {/* Aprobado */}
          {aprobadoProjects.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                  Aprobado
                  <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                    {aprobadoProjects.length}
                  </span>
                </h2>
              </div>
              {!isResidente && (
                <Card className="hidden sm:block">
                  <div className="max-h-96 overflow-x-auto overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          <th className="px-4 py-3">Proyecto</th>
                          <th className="px-4 py-3">Rubros</th>
                          <th className="px-4 py-3">Residente</th>
                          <th className="px-4 py-3">Estado</th>
                          <th className="w-10 px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {aprobadoProjects.map((project) => (
                          <ProjectRow key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
              <div className={`${isResidente ? 'space-y-3' : 'space-y-3 sm:hidden'} max-h-96 overflow-y-auto pr-1`}>
                {aprobadoProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                ))}
              </div>
            </div>
          )}

          {/* Pre Liquidación */}
          {preliquidacionProjects.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-orange-500" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                  Pre Liquidación
                  <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                    {preliquidacionProjects.length}
                  </span>
                </h2>
              </div>
              {!isResidente && (
                <Card className="hidden sm:block">
                  <div className="max-h-96 overflow-x-auto overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          <th className="px-4 py-3">Proyecto</th>
                          <th className="px-4 py-3">Rubros</th>
                          <th className="px-4 py-3">Residente</th>
                          <th className="px-4 py-3">Estado</th>
                          <th className="w-10 px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {preliquidacionProjects.map((project) => (
                          <ProjectRow key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
              <div className={`${isResidente ? 'space-y-3' : 'space-y-3 sm:hidden'} max-h-96 overflow-y-auto pr-1`}>
                {preliquidacionProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                ))}
              </div>
            </div>
          )}

          {/* Contacto Inicial */}
          {inicioProjects.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                  Contacto Inicial
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {inicioProjects.length}
                  </span>
                </h2>
              </div>
              {!isResidente && (
                <Card className="hidden sm:block">
                  <div className="max-h-96 overflow-x-auto overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          <th className="px-4 py-3">Proyecto</th>
                          <th className="px-4 py-3">Rubros</th>
                          <th className="px-4 py-3">Residente</th>
                          <th className="px-4 py-3">Estado</th>
                          <th className="w-10 px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {inicioProjects.map((project) => (
                          <ProjectRow key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
              <div className={`${isResidente ? 'space-y-3' : 'space-y-3 sm:hidden'} max-h-96 overflow-y-auto pr-1`}>
                {inicioProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                ))}
              </div>
            </div>
          )}

          {/* Finalizado */}
          {finalizadoProjects.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-purple-500" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                  Finalizado
                  <span className="ml-2 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                    {finalizadoProjects.length}
                  </span>
                </h2>
              </div>
              {!isResidente && (
                <Card className="hidden sm:block">
                  <div className="max-h-96 overflow-x-auto overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          <th className="px-4 py-3">Proyecto</th>
                          <th className="px-4 py-3">Rubros</th>
                          <th className="px-4 py-3">Residente</th>
                          <th className="px-4 py-3">Estado</th>
                          <th className="w-10 px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {finalizadoProjects.map((project) => (
                          <ProjectRow key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
              <div className={`${isResidente ? 'space-y-3' : 'space-y-3 sm:hidden'} max-h-96 overflow-y-auto pr-1`}>
                {finalizadoProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} onClick={() => navigate(`/proyectos/${project.id}`)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile FAB */}
      {canCreate && (
        <button
          className="fixed bottom-6 right-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#dd5113] text-white shadow-lg transition-transform hover:scale-105 sm:hidden"
          onClick={() => navigate('/proyectos/nuevo')}
          aria-label="Nuevo Proyecto"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
}

// ── Desktop table row ──────────────────────────────────────────────
function ProjectRow({ project, onClick }: { project: ProjectListDTO; onClick: () => void }) {
  const deleteMutation = useDeleteProject();
  const user = useAuthStore((s) => s.user);
  const advancedStates = ['en_ejecucion', 'pre_liquidacion', 'finalizado'];
  const isAdvanced = advancedStates.includes(project.estado);
  const canDelete = ['contacto_inicial', 'datos_listos'].includes(project.estado) ||
    (isAdvanced && ['gerente', 'operaciones'].includes(user?.role ?? ''));
  return (
    <tr
      className="cursor-pointer border-b border-gray-50 transition-colors hover:bg-gray-50/50"
      onClick={onClick}
    >
      <td className="px-4 py-3">
        <div>
          <p className="font-medium text-gray-800">{project.nombre}</p>
          <p className="text-xs text-gray-400">{project.codigo} — {project.client.nombre}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          {project.tiene_gypsum && <Badge color="blue">G</Badge>}
          {project.tiene_pintura && <Badge color="green">P</Badge>}
          {project.tiene_impermeabilizacion && <Badge color="orange">I</Badge>}
        </div>
      </td>
      <td className="px-4 py-3 text-gray-600">{project.residente.nombre}</td>
      <td className="px-4 py-3">
        <StatusBadge entity="project" status={project.estado} />
      </td>
      <td className="px-4 py-3">
        {canDelete && (
          <button
            className="text-gray-400 hover:text-red-500"
            title="Eliminar proyecto"
            onClick={async (e) => {
              e.stopPropagation();
              const msg = isAdvanced
                ? `ADVERTENCIA: Este proyecto está en ejecución. Se eliminarán permanentemente todas las planillas, pedidos, OT y datos asociados.\n\n¿Eliminar permanentemente "${project.nombre}"?`
                : '¿Eliminar este proyecto? Esta acción no se puede deshacer.';
              if (window.confirm(msg)) {
                await deleteMutation.mutateAsync(project.id);
              }
            }}
          >
            <Trash2 size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}

// ── Mobile / residente card ────────────────────────────────────────────────
function ProjectCard({ project, onClick }: { project: ProjectListDTO; onClick: () => void }) {
  const deleteMutation = useDeleteProject();
  const user = useAuthStore((s) => s.user);
  const advancedStates = ['en_ejecucion', 'pre_liquidacion', 'finalizado'];
  const isAdvanced = advancedStates.includes(project.estado);
  const canDelete = ['contacto_inicial', 'datos_listos'].includes(project.estado) ||
    (isAdvanced && ['gerente', 'operaciones'].includes(user?.role ?? ''));

  return (
    <div className="glass-card relative w-full transition-colors hover:bg-white/70">
      <button className="block w-full p-4 text-left" onClick={onClick}>
        <div className="flex items-start justify-between pr-6">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-800">{project.nombre}</p>
            <p className="text-xs text-gray-400">{project.codigo} — {project.client.nombre}</p>
          </div>
          <StatusBadge entity="project" status={project.estado} />
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {project.tiene_gypsum && <Badge color="blue">Gypsum</Badge>}
          {project.tiene_pintura && <Badge color="green">Pintura</Badge>}
          {project.tiene_impermeabilizacion && <Badge color="orange">Impermeab.</Badge>}
        </div>
      </button>
      {canDelete && (
        <button
          className="absolute right-3 top-3 p-1 text-gray-300 hover:text-red-500"
          title="Eliminar proyecto"
          onClick={async (e) => {
            e.stopPropagation();
            const msg = isAdvanced
              ? `ADVERTENCIA: Este proyecto está en ejecución. Se eliminarán permanentemente todas las planillas, pedidos, OT y datos asociados.\n\n¿Eliminar permanentemente "${project.nombre}"?`
              : '¿Eliminar este proyecto? Esta acción no se puede deshacer.';
            if (window.confirm(msg)) {
              await deleteMutation.mutateAsync(project.id);
            }
          }}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}
